import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, FileText, AlertCircle, CheckCircle, XCircle, Scale, User, Sparkles, Globe, ExternalLink, Mail, Shield } from 'lucide-react';

// Panchi Bird Logo
const PanchiBirdLogo = ({ className = "w-10 h-10" }) => (
  <img 
    src="https://customer-assets.emergentagent.com/job_panchi-travel-1/artifacts/5hhgrsb0_20260119_170240_0000.png"
    alt="Panchi"
    className={className}
    style={{ objectFit: 'contain' }}
  />
);

export default function TermsOfService() {
  const navigate = useNavigate();
  const lastUpdated = "January 24, 2026";
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-teal-50 pb-8">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-500 hover:text-teal-600 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft size={20} />
          </button>
          
          <div className="flex items-center gap-2">
            <PanchiBirdLogo className="w-8 h-8" />
            <span className="font-bold text-gray-900">Terms of Service</span>
          </div>
          
          <div className="w-8" />
        </div>
      </header>
      
      <main className="max-w-4xl mx-auto px-4 md:px-6 py-8">
        {/* Hero */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="w-16 h-16 bg-amber-100 flex items-center justify-center mx-auto mb-6" style={{ borderRadius: '1rem' }}>
            <FileText size={32} className="text-amber-600" />
          </div>
          <h1 className="text-3xl md:text-4xl font-black text-gray-900 mb-4">
            Terms of Service
          </h1>
          <p className="text-gray-500 max-w-xl mx-auto">
            Panchi – AI Travel Co-Planner
          </p>
          <p className="text-sm text-gray-400 mt-4">Last updated: {lastUpdated}</p>
        </motion.div>
        
        {/* Content Sections */}
        <div className="space-y-8">
          {/* 1. Acceptance of Terms */}
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-6 bg-white/80 backdrop-blur border border-gray-100 shadow"
            style={{ borderRadius: '1.25rem' }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-emerald-100 flex items-center justify-center" style={{ borderRadius: '0.75rem' }}>
                <CheckCircle size={20} className="text-emerald-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">1. Acceptance of Terms</h2>
            </div>
            <p className="text-gray-600 mb-3">
              By accessing or using Panchi, you agree to these Terms of Service.
            </p>
            <p className="text-gray-600">
              If you do not agree, please do not use the platform.
            </p>
          </motion.section>

          {/* 2. Service Description */}
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="p-6 bg-white/80 backdrop-blur border border-gray-100 shadow"
            style={{ borderRadius: '1.25rem' }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-teal-100 flex items-center justify-center" style={{ borderRadius: '0.75rem' }}>
                <Globe size={20} className="text-teal-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">2. Service Description</h2>
            </div>
            <p className="text-gray-600 mb-3">
              Panchi provides AI-powered travel planning, recommendations, and discovery tools.
            </p>
            <p className="text-gray-600 mb-2">Panchi:</p>
            <ul className="space-y-1 text-gray-600 ml-4">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-teal-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Does not sell tickets
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-teal-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Does not guarantee prices or availability
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-teal-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Redirects users to official or third-party platforms for bookings
              </li>
            </ul>
          </motion.section>

          {/* 3. Open Beta Disclaimer */}
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="p-6 bg-amber-50 border border-amber-200"
            style={{ borderRadius: '1.25rem' }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-amber-100 flex items-center justify-center" style={{ borderRadius: '0.75rem' }}>
                <AlertCircle size={20} className="text-amber-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">3. Open Beta Disclaimer</h2>
            </div>
            <p className="text-gray-700 mb-3">
              Panchi may be offered as an <strong>Open Beta</strong>.
            </p>
            <p className="text-gray-700 mb-2">This means:</p>
            <ul className="space-y-1 text-gray-700 ml-4">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-amber-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Features may change
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-amber-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Occasional errors may occur
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-amber-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Feedback is encouraged
              </li>
            </ul>
            <p className="text-amber-700 mt-3 font-medium">
              We appreciate your understanding during this phase.
            </p>
          </motion.section>

          {/* 4. User Accounts */}
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="p-6 bg-white/80 backdrop-blur border border-gray-100 shadow"
            style={{ borderRadius: '1.25rem' }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-teal-100 flex items-center justify-center" style={{ borderRadius: '0.75rem' }}>
                <User size={20} className="text-teal-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">4. User Accounts</h2>
            </div>
            <p className="text-gray-600 mb-2">You are responsible for:</p>
            <ul className="space-y-1 text-gray-600 ml-4">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-teal-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Keeping your login credentials secure
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-teal-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                All activity under your account
              </li>
            </ul>
            <p className="text-gray-600 mt-3">
              We reserve the right to suspend accounts for misuse or abuse.
            </p>
          </motion.section>

          {/* 5. Acceptable Use */}
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="p-6 bg-white/80 backdrop-blur border border-gray-100 shadow"
            style={{ borderRadius: '1.25rem' }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 flex items-center justify-center" style={{ borderRadius: '0.75rem' }}>
                <XCircle size={20} className="text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">5. Acceptable Use</h2>
            </div>
            <p className="text-gray-600 mb-2">You agree not to:</p>
            <ul className="space-y-1 text-gray-600 ml-4">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-red-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Use Panchi for unlawful purposes
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-red-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Attempt to access other users' data
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-red-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Scrape or misuse the platform
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-red-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Interfere with system integrity
              </li>
            </ul>
          </motion.section>

          {/* 6. AI-Generated Content */}
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="p-6 bg-white/80 backdrop-blur border border-gray-100 shadow"
            style={{ borderRadius: '1.25rem' }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-purple-100 flex items-center justify-center" style={{ borderRadius: '0.75rem' }}>
                <Sparkles size={20} className="text-purple-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">6. AI-Generated Content</h2>
            </div>
            <p className="text-gray-600 mb-3">
              Travel plans and recommendations are generated by AI.
            </p>
            <p className="text-gray-600 mb-2">You acknowledge that:</p>
            <ul className="space-y-1 text-gray-600 ml-4">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-purple-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Results are informational
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-purple-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Final decisions are your responsibility
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-purple-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Real-world conditions may change
              </li>
            </ul>
            <p className="text-gray-700 font-medium mt-3">
              Always verify details before booking or traveling.
            </p>
          </motion.section>

          {/* 7. Third-Party Links & Services */}
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="p-6 bg-white/80 backdrop-blur border border-gray-100 shadow"
            style={{ borderRadius: '1.25rem' }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-teal-100 flex items-center justify-center" style={{ borderRadius: '0.75rem' }}>
                <ExternalLink size={20} className="text-teal-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">7. Third-Party Links & Services</h2>
            </div>
            <p className="text-gray-600 mb-2">Panchi may link to:</p>
            <ul className="space-y-1 text-gray-600 ml-4">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-teal-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Airlines
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-teal-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Hotels
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-teal-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Event platforms
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-teal-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Payment services
              </li>
            </ul>
            <p className="text-gray-600 mt-3">
              We are not responsible for third-party content, pricing, or services.
            </p>
          </motion.section>

          {/* 8. Intellectual Property */}
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="p-6 bg-white/80 backdrop-blur border border-gray-100 shadow"
            style={{ borderRadius: '1.25rem' }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-teal-100 flex items-center justify-center" style={{ borderRadius: '0.75rem' }}>
                <Shield size={20} className="text-teal-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">8. Intellectual Property</h2>
            </div>
            <p className="text-gray-600 mb-3">
              All content, branding, and software related to Panchi belong to Panchi unless stated otherwise.
            </p>
            <p className="text-gray-600">
              You may not copy or redistribute without permission.
            </p>
          </motion.section>

          {/* 9. Limitation of Liability */}
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 }}
            className="p-6 bg-white/80 backdrop-blur border border-gray-100 shadow"
            style={{ borderRadius: '1.25rem' }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-gray-100 flex items-center justify-center" style={{ borderRadius: '0.75rem' }}>
                <Scale size={20} className="text-gray-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">9. Limitation of Liability</h2>
            </div>
            <p className="text-gray-600 mb-2">To the fullest extent permitted by law:</p>
            <ul className="space-y-1 text-gray-600 ml-4">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-gray-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Panchi is not liable for travel disruptions, losses, or damages
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-gray-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Use of the platform is at your own risk
              </li>
            </ul>
          </motion.section>

          {/* 10. Termination */}
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="p-6 bg-white/80 backdrop-blur border border-gray-100 shadow"
            style={{ borderRadius: '1.25rem' }}
          >
            <h2 className="text-xl font-bold text-gray-900 mb-4">10. Termination</h2>
            <p className="text-gray-600 mb-2">We may suspend or terminate access if:</p>
            <ul className="space-y-1 text-gray-600 ml-4">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-gray-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Terms are violated
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-gray-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                The platform is misused
              </li>
            </ul>
            <p className="text-gray-600 mt-3">
              You may stop using Panchi at any time.
            </p>
          </motion.section>

          {/* 11. Governing Law */}
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55 }}
            className="p-6 bg-white/80 backdrop-blur border border-gray-100 shadow"
            style={{ borderRadius: '1.25rem' }}
          >
            <h2 className="text-xl font-bold text-gray-900 mb-4">11. Governing Law</h2>
            <p className="text-gray-600">
              These terms are governed by the laws of <strong>India</strong>, unless otherwise required by applicable law.
            </p>
          </motion.section>

          {/* 12. Contact */}
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="p-6 bg-amber-50 border border-amber-100"
            style={{ borderRadius: '1.25rem' }}
          >
            <h2 className="text-xl font-bold text-gray-900 mb-4">12. Contact</h2>
            <p className="text-gray-600 mb-4">For questions or concerns:</p>
            <a 
              href="mailto:admin@panchi.world"
              className="inline-flex items-center gap-2 px-6 py-3 bg-amber-600 text-white font-semibold hover:bg-amber-700 transition-colors"
              style={{ borderRadius: '9999px' }}
            >
              <Mail size={18} />
              admin@panchi.world
            </a>
          </motion.section>
        </div>
        
        {/* Links */}
        <div className="mt-8 text-center">
          <Link to="/privacy" className="text-teal-600 font-medium hover:text-teal-500">
            View Privacy Policy →
          </Link>
        </div>
      </main>
    </div>
  );
}
